import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Edit,
  Save,
  X,
  Camera,
  Star,
  Award,
  Briefcase,
  Code,
  DollarSign,
  Plus,
  Trash2,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';

interface UserProfile {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  profileImage?: string;
  phone?: string;
  location?: string;
  bio?: string;
  skills: string[];
  experience: string;
  hourlyRate: {
    amount: number;
    currency: string;
  };
  availability: string;
  portfolio: {
    _id: string;
    title: string;
    description: string;
    url: string;
    technologies: string[];
    completedAt: string;
  }[];
  bankDetails?: {
    accountName: string;
    accountNumber: string;
    bankName: string;
    routingNumber: string;
    currency: string;
  };
  preferences: {
    notifications: {
      email: boolean;
      push: boolean;
    };
    language: string;
    timezone: string;
  };
  createdAt: string;
  lastActive: string;
}

// Get the correct API base URL based on environment
const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};

const UserProfile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<
    'overview' | 'skills' | 'portfolio' | 'settings' | 'add-portfolio'
  >('overview');

  const [newSkill, setNewSkill] = useState('');
  const [newTechnology, setNewTechnology] = useState('');
  const [newPortfolioItem, setNewPortfolioItem] = useState({
    title: '',
    description: '',
    url: '',
    technologies: [] as string[],
    completedAt: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const API_BASE_URL = getApiBaseUrl();

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/users/${user?._id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data.user);
      } else {
        // If API call fails, use the user data from context
        if (user) {
          setProfile({
            _id: user._id,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role,
            profileImage: user.profileImage,
            skills: user.skills || [],
            experience: user.experience || 'junior',
            hourlyRate: user.hourlyRate || { amount: 0, currency: 'USD' },
            availability: user.availability || 'available',
            portfolio: [],
            preferences: user.preferences || {
              notifications: { email: true, push: true },
              language: 'en',
              timezone: 'UTC',
            },
            createdAt: new Date().toISOString(),
            lastActive: new Date().toISOString(),
          });
        }
      }
    } catch (error) {
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profile) return;
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/users/${profile._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(profile),
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data.user);
        updateUser(data.user);
        setEditing(false);
        setSuccess('Profile updated successfully');
      } else {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update profile');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to update profile');
    }
  };

  const addSkill = () => {
    if (
      newSkill.trim() &&
      profile &&
      !profile.skills.includes(newSkill.trim())
    ) {
      setProfile({
        ...profile,
        skills: [...profile.skills, newSkill.trim()],
      });
      setNewSkill('');
    }
  };

  const removeSkill = (skill: string) => {
    if (profile) {
      setProfile({
        ...profile,
        skills: profile.skills.filter((s) => s !== skill),
      });
    }
  };

  const addTechnologyToPortfolioItem = () => {
    if (
      newTechnology.trim() &&
      !newPortfolioItem.technologies.includes(newTechnology.trim())
    ) {
      setNewPortfolioItem({
        ...newPortfolioItem,
        technologies: [...newPortfolioItem.technologies, newTechnology.trim()],
      });
      setNewTechnology('');
    }
  };

  const removeTechnologyFromPortfolioItem = (tech: string) => {
    setNewPortfolioItem({
      ...newPortfolioItem,
      technologies: newPortfolioItem.technologies.filter((t) => t !== tech),
    });
  };

  const addPortfolioItem = () => {
    if (profile && newPortfolioItem.title && newPortfolioItem.description) {
      setProfile({
        ...profile,
        portfolio: [
          ...profile.portfolio,
          {
            _id: Date.now().toString(),
            ...newPortfolioItem,
          },
        ],
      });
      setNewPortfolioItem({
        title: '',
        description: '',
        url: '',
        technologies: [],
        completedAt: '',
      });
    }
  };

  const removePortfolioItem = (id: string) => {
    if (profile) {
      setProfile({
        ...profile,
        portfolio: profile.portfolio.filter((item) => item._id !== id),
      });
    }
  };

  const getExperienceColor = (experience: string) => {
    switch (experience) {
      case 'junior':
        return 'text-green-600 bg-green-100';
      case 'mid':
        return 'text-blue-600 bg-blue-100';
      case 'senior':
        return 'text-purple-600 bg-purple-100';
      case 'expert':
        return 'text-orange-600 bg-orange-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getAvailabilityColor = (availability: string) => {
    switch (availability) {
      case 'available':
        return 'text-green-600 bg-green-100';
      case 'busy':
        return 'text-yellow-600 bg-yellow-100';
      case 'unavailable':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  // Mock data for demonstration
  const mockProfile: UserProfile = {
    _id: user?._id || '1',
    email: user?.email || 'john.doe@example.com',
    firstName: user?.firstName || 'John',
    lastName: user?.lastName || 'Doe',
    role: user?.role || 'developer',
    phone: '+1 (555) 123-4567',
    location: 'San Francisco, CA',
    bio: 'Full-stack developer with 5 years of experience in React, Node.js, and cloud technologies.',
    skills: ['React', 'Node.js', 'TypeScript', 'MongoDB', 'AWS'],
    experience: 'senior',
    hourlyRate: {
      amount: 75,
      currency: 'USD',
    },
    availability: 'available',
    portfolio: [
      {
        _id: '1',
        title: 'E-commerce Platform',
        description: 'Full-stack e-commerce solution with React and Node.js',
        url: 'https://example.com',
        technologies: ['React', 'Node.js', 'MongoDB'],
        completedAt: '2024-01-15',
      },
      {
        _id: '2',
        title: 'Task Management App',
        description: 'Real-time task management application',
        url: 'https://example.com',
        technologies: ['Vue.js', 'Express', 'Socket.io'],
        completedAt: '2024-02-20',
      },
    ],
    preferences: {
      notifications: {
        email: true,
        push: true,
      },
      language: 'en',
      timezone: 'UTC',
    },
    createdAt: '2023-06-01T00:00:00.000Z',
    lastActive: new Date().toISOString(),
  };

  const displayProfile = profile || mockProfile;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border animate-pulse">
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 bg-gray-200 rounded-full"></div>
            <div className="flex-1">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-2xl">
                    {displayProfile.firstName[0]}
                    {displayProfile.lastName[0]}
                  </span>
                </div>
                <button className="absolute bottom-0 right-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors duration-200">
                  <Camera className="w-3 h-3" />
                </button>
              </div>

              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {displayProfile.firstName} {displayProfile.lastName}
                </h1>
                <p className="text-gray-600 capitalize">
                  {displayProfile.role}
                </p>
                <div className="flex items-center space-x-4 mt-2">
                  {displayProfile.role === 'developer' && (
                    <>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getExperienceColor(
                          displayProfile.experience
                        )}`}
                      >
                        {displayProfile.experience} Level
                      </span>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getAvailabilityColor(
                          displayProfile.availability
                        )}`}
                      >
                        {displayProfile.availability}
                      </span>
                      <span className="text-sm text-gray-600">
                        {formatCurrency(
                          displayProfile.hourlyRate.amount,
                          displayProfile.hourlyRate.currency
                        )}
                        /hour
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <button
              onClick={() => (editing ? handleSaveProfile() : setEditing(true))}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              {editing ? (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              ) : (
                <>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Profile
                </>
              )}
            </button>
          </div>

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            </div>
          )}

          {success && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                <span className="text-green-700 text-sm">{success}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', name: 'Overview', icon: User },
              { id: 'skills', name: 'Skills & Experience', icon: Code },
              { id: 'portfolio', name: 'Portfolio', icon: Briefcase },
              { id: 'settings', name: 'Settings', icon: Edit },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as typeof activeTab)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Contact Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Mail className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-700">
                        {displayProfile.email}
                      </span>
                    </div>

                    {displayProfile.phone && (
                      <div className="flex items-center space-x-3">
                        <Phone className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-700">
                          {displayProfile.phone}
                        </span>
                      </div>
                    )}

                    {displayProfile.location && (
                      <div className="flex items-center space-x-3">
                        <MapPin className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-700">
                          {displayProfile.location}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center space-x-3">
                      <Calendar className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-700">
                        Joined{' '}
                        {new Date(
                          displayProfile.createdAt
                        ).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>

                {displayProfile.role === 'developer' && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Professional Info
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <Star className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-700 capitalize">
                          {displayProfile.experience} Level
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <DollarSign className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-700">
                          {formatCurrency(
                            displayProfile.hourlyRate.amount,
                            displayProfile.hourlyRate.currency
                          )}
                          /hour
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Award className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-700 capitalize">
                          {displayProfile.availability}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {displayProfile.bio && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Bio
                  </h3>
                  <p className="text-gray-700">{displayProfile.bio}</p>
                </div>
              )}

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Skills
                </h3>
                <div className="flex flex-wrap gap-2">
                  {displayProfile.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'skills' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Skills
                </h3>

                {editing && (
                  <div className="mb-4">
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={newSkill}
                        onChange={(e) => setNewSkill(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addSkill()}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Add a skill"
                      />
                      <button
                        onClick={addSkill}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  {displayProfile.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                    >
                      {skill}
                      {editing && (
                        <button
                          onClick={() => removeSkill(skill)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              </div>

              {displayProfile.role === 'developer' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Experience Level
                    </label>
                    {editing ? (
                      <select
                        value={displayProfile.experience}
                        onChange={(e) =>
                          setProfile((prev) =>
                            prev
                              ? { ...prev, experience: e.target.value }
                              : null
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="junior">Junior</option>
                        <option value="mid">Mid-level</option>
                        <option value="senior">Senior</option>
                        <option value="expert">Expert</option>
                      </select>
                    ) : (
                      <p className="text-gray-900 capitalize">
                        {displayProfile.experience}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hourly Rate
                    </label>
                    {editing ? (
                      <div className="flex space-x-2">
                        <input
                          type="number"
                          value={displayProfile.hourlyRate.amount}
                          onChange={(e) =>
                            setProfile((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    hourlyRate: {
                                      ...prev.hourlyRate,
                                      amount: parseFloat(e.target.value) || 0,
                                    },
                                  }
                                : null
                            )
                          }
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <select
                          value={displayProfile.hourlyRate.currency}
                          onChange={(e) =>
                            setProfile((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    hourlyRate: {
                                      ...prev.hourlyRate,
                                      currency: e.target.value,
                                    },
                                  }
                                : null
                            )
                          }
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                        </select>
                      </div>
                    ) : (
                      <p className="text-gray-900">
                        {formatCurrency(
                          displayProfile.hourlyRate.amount,
                          displayProfile.hourlyRate.currency
                        )}
                        /hour
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Availability
                    </label>
                    {editing ? (
                      <select
                        value={displayProfile.availability}
                        onChange={(e) =>
                          setProfile((prev) =>
                            prev
                              ? { ...prev, availability: e.target.value }
                              : null
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="available">Available</option>
                        <option value="busy">Busy</option>
                        <option value="unavailable">Unavailable</option>
                      </select>
                    ) : (
                      <p className="text-gray-900 capitalize">
                        {displayProfile.availability}
                      </p>
                    )}
                  </div>

                  {editing && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={displayProfile.phone || ''}
                        onChange={(e) =>
                          setProfile((prev) =>
                            prev ? { ...prev, phone: e.target.value } : null
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  )}

                  {editing && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Location
                      </label>
                      <input
                        type="text"
                        value={displayProfile.location || ''}
                        onChange={(e) =>
                          setProfile((prev) =>
                            prev ? { ...prev, location: e.target.value } : null
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="City, Country"
                      />
                    </div>
                  )}
                </div>
              )}

              {editing && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Professional Bio
                  </label>
                  <textarea
                    value={displayProfile.bio || ''}
                    onChange={(e) =>
                      setProfile((prev) =>
                        prev ? { ...prev, bio: e.target.value } : null
                      )
                    }
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Tell us about your experience and expertise..."
                  />
                </div>
              )}
            </div>
          )}

          {activeTab === 'portfolio' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Portfolio
                </h3>
                {editing && (
                  <button
                    onClick={() => setActiveTab('add-portfolio')}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Project
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {displayProfile.portfolio.length === 0 ? (
                  <div className="md:col-span-2 text-center py-8">
                    <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">
                      No portfolio items yet
                    </h4>
                    <p className="text-gray-600 mb-4">
                      Showcase your work by adding portfolio projects
                    </p>
                    {editing && (
                      <button
                        onClick={() => setActiveTab('add-portfolio')}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Project
                      </button>
                    )}
                  </div>
                ) : (
                  displayProfile.portfolio.map((item) => (
                    <div
                      key={item._id}
                      className="border border-gray-200 rounded-lg p-6"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <h4 className="font-semibold text-gray-900">
                          {item.title}
                        </h4>
                        {editing && (
                          <button
                            onClick={() => removePortfolioItem(item._id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      <p className="text-gray-600 mb-4">{item.description}</p>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {item.technologies.map((tech, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        {item.url && (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700"
                          >
                            View Project
                          </a>
                        )}
                        <span className="text-gray-500">
                          {new Date(item.completedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'add-portfolio' && (
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                Add Portfolio Item
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Title *
                  </label>
                  <input
                    type="text"
                    value={newPortfolioItem.title}
                    onChange={(e) =>
                      setNewPortfolioItem({
                        ...newPortfolioItem,
                        title: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="E.g., E-commerce Website"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={newPortfolioItem.description}
                    onChange={(e) =>
                      setNewPortfolioItem({
                        ...newPortfolioItem,
                        description: e.target.value,
                      })
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Describe the project and your role..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project URL
                  </label>
                  <input
                    type="url"
                    value={newPortfolioItem.url}
                    onChange={(e) =>
                      setNewPortfolioItem({
                        ...newPortfolioItem,
                        url: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Completion Date
                  </label>
                  <input
                    type="date"
                    value={newPortfolioItem.completedAt}
                    onChange={(e) =>
                      setNewPortfolioItem({
                        ...newPortfolioItem,
                        completedAt: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Technologies Used
                  </label>
                  <div className="flex space-x-2 mb-2">
                    <input
                      type="text"
                      value={newTechnology}
                      onChange={(e) => setNewTechnology(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === 'Enter' &&
                        (e.preventDefault(), addTechnologyToPortfolioItem())
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="E.g., React, Node.js"
                    />
                    <button
                      type="button"
                      onClick={addTechnologyToPortfolioItem}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-2">
                    {newPortfolioItem.technologies.map((tech, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-full"
                      >
                        {tech}
                        <button
                          type="button"
                          onClick={() =>
                            removeTechnologyFromPortfolioItem(tech)
                          }
                          className="ml-2 text-gray-600 hover:text-gray-800"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setActiveTab('portfolio')}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={addPortfolioItem}
                    disabled={
                      !newPortfolioItem.title || !newPortfolioItem.description
                    }
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    Add Project
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Notification Preferences
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">
                        Email Notifications
                      </h4>
                      <p className="text-sm text-gray-600">
                        Receive notifications via email
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={displayProfile.preferences.notifications.email}
                        onChange={(e) =>
                          setProfile((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  preferences: {
                                    ...prev.preferences,
                                    notifications: {
                                      ...prev.preferences.notifications,
                                      email: e.target.checked,
                                    },
                                  },
                                }
                              : null
                          )
                        }
                        className="sr-only peer"
                        disabled={!editing}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">
                        Push Notifications
                      </h4>
                      <p className="text-sm text-gray-600">
                        Receive push notifications in browser
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={displayProfile.preferences.notifications.push}
                        onChange={(e) =>
                          setProfile((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  preferences: {
                                    ...prev.preferences,
                                    notifications: {
                                      ...prev.preferences.notifications,
                                      push: e.target.checked,
                                    },
                                  },
                                }
                              : null
                          )
                        }
                        className="sr-only peer"
                        disabled={!editing}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Language
                  </label>
                  {editing ? (
                    <select
                      value={displayProfile.preferences.language}
                      onChange={(e) =>
                        setProfile((prev) =>
                          prev
                            ? {
                                ...prev,
                                preferences: {
                                  ...prev.preferences,
                                  language: e.target.value,
                                },
                              }
                            : null
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                    </select>
                  ) : (
                    <p className="text-gray-900">
                      {displayProfile.preferences.language === 'en'
                        ? 'English'
                        : displayProfile.preferences.language === 'es'
                        ? 'Spanish'
                        : displayProfile.preferences.language === 'fr'
                        ? 'French'
                        : displayProfile.preferences.language === 'de'
                        ? 'German'
                        : 'English'}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timezone
                  </label>
                  {editing ? (
                    <select
                      value={displayProfile.preferences.timezone}
                      onChange={(e) =>
                        setProfile((prev) =>
                          prev
                            ? {
                                ...prev,
                                preferences: {
                                  ...prev.preferences,
                                  timezone: e.target.value,
                                },
                              }
                            : null
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">Eastern Time</option>
                      <option value="America/Chicago">Central Time</option>
                      <option value="America/Denver">Mountain Time</option>
                      <option value="America/Los_Angeles">Pacific Time</option>
                      <option value="Europe/London">London</option>
                      <option value="Europe/Paris">Paris</option>
                    </select>
                  ) : (
                    <p className="text-gray-900">
                      {displayProfile.preferences.timezone}
                    </p>
                  )}
                </div>
              </div>

              {displayProfile.role === 'developer' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Payment Information
                  </h3>

                  {editing ? (
                    <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Account Name
                          </label>
                          <input
                            type="text"
                            value={
                              displayProfile.bankDetails?.accountName || ''
                            }
                            onChange={(e) =>
                              setProfile((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      bankDetails: {
                                        ...(prev.bankDetails || {
                                          accountNumber: '',
                                          bankName: '',
                                          routingNumber: '',
                                          currency: 'USD',
                                        }),
                                        accountName: e.target.value,
                                      },
                                    }
                                  : null
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Full name on account"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Account Number
                          </label>
                          <input
                            type="text"
                            value={
                              displayProfile.bankDetails?.accountNumber || ''
                            }
                            onChange={(e) =>
                              setProfile((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      bankDetails: {
                                        ...(prev.bankDetails || {
                                          accountName: '',
                                          bankName: '',
                                          routingNumber: '',
                                          currency: 'USD',
                                        }),
                                        accountNumber: e.target.value,
                                      },
                                    }
                                  : null
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Account number"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Bank Name
                          </label>
                          <input
                            type="text"
                            value={displayProfile.bankDetails?.bankName || ''}
                            onChange={(e) =>
                              setProfile((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      bankDetails: {
                                        ...(prev.bankDetails || {
                                          accountName: '',
                                          accountNumber: '',
                                          routingNumber: '',
                                          currency: 'USD',
                                        }),
                                        bankName: e.target.value,
                                      },
                                    }
                                  : null
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Bank name"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Routing Number
                          </label>
                          <input
                            type="text"
                            value={
                              displayProfile.bankDetails?.routingNumber || ''
                            }
                            onChange={(e) =>
                              setProfile((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      bankDetails: {
                                        ...(prev.bankDetails || {
                                          accountName: '',
                                          accountNumber: '',
                                          bankName: '',
                                          currency: 'USD',
                                        }),
                                        routingNumber: e.target.value,
                                      },
                                    }
                                  : null
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Routing number"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {displayProfile.bankDetails ? (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm text-gray-600">
                                Account Name
                              </p>
                              <p className="font-medium text-gray-900">
                                {displayProfile.bankDetails.accountName}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Bank</p>
                              <p className="font-medium text-gray-900">
                                {displayProfile.bankDetails.bankName}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">
                                Account Number
                              </p>
                              <p className="font-medium text-gray-900">
                                ••••
                                {displayProfile.bankDetails.accountNumber.slice(
                                  -4
                                )}
                              </p>
                            </div>
                            {displayProfile.bankDetails.routingNumber && (
                              <div>
                                <p className="text-sm text-gray-600">
                                  Routing Number
                                </p>
                                <p className="font-medium text-gray-900">
                                  ••••
                                  {displayProfile.bankDetails.routingNumber.slice(
                                    -4
                                  )}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-600">
                          No payment information added yet.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
